import type { PersonaScriptDto } from "@/features/persona-opportunities/api/persona-opportunity-scripts.api";

/** The fields hook-variant ordering reads — shared by workspace scripts and hub rows. */
export interface HookVariantOrderable {
  id: string;
  slotIndex: number;
  parentScriptId?: string | null;
}

export function isPersonaScriptHookVariant(script: PersonaScriptDto): boolean {
  return Boolean(script.parentScriptId);
}

/** Roots by slot, then each parent’s variants immediately after it. */
export function sortPersonaScriptsWithVariantsAfterParent<T extends HookVariantOrderable>(
  scripts: readonly T[],
): T[] {
  const ids = new Set(scripts.map((script) => script.id));
  const childrenByParent = new Map<string, T[]>();
  const roots: T[] = [];

  for (const script of scripts) {
    const parentId = script.parentScriptId;
    if (parentId && ids.has(parentId)) {
      const children = childrenByParent.get(parentId) ?? [];
      children.push(script);
      childrenByParent.set(parentId, children);
      continue;
    }
    roots.push(script);
  }

  roots.sort(compareBySlotThenId);

  const ordered: T[] = [];
  for (const root of roots) {
    ordered.push(root);
    const children = childrenByParent.get(root.id);
    if (!children) continue;
    children.sort(compareBySlotThenId);
    ordered.push(...children);
  }
  return ordered;
}

export function getHookVariantDisplayMeta(
  script: HookVariantOrderable,
  scripts: readonly HookVariantOrderable[],
): { parentSlot: number; ordinal: number } | null {
  if (!script.parentScriptId) return null;
  const parent = scripts.find((item) => item.id === script.parentScriptId);
  const variants = scripts
    .filter((item) => item.parentScriptId === script.parentScriptId)
    .sort(compareBySlotThenId);
  const index = variants.findIndex((item) => item.id === script.id);
  if (index < 0) return null;
  return {
    parentSlot: parent?.slotIndex ?? script.slotIndex,
    ordinal: index + 1,
  };
}

function compareBySlotThenId(a: HookVariantOrderable, b: HookVariantOrderable): number {
  return a.slotIndex - b.slotIndex || a.id.localeCompare(b.id);
}
